import { useLocale } from '@pperform/pp-react-components/PpLocaleProvider';
import {
  convertTrackingEventDateTime,
  formatDateTime,
} from '@pperform/pp-utils/datetime.utils';
import { useState, useRef, useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import { useGlobalConfigContext } from 'GlobalConfig.context';
import CloseButton from 'components/CloseButton';
import { CustomText } from 'components/CustomText';
import { addAriaLabelToAnchors } from 'components/CustomText/CustomText.util';
import { useCarrierInformation } from 'components/MerchantTrackingWidget/context';
import { Body as BodyModal, Header as HeaderModal } from 'components/Modal';
import Typography from 'components/Typography';
import CONFIGURABLE_CONSTANTS from 'constants/configurable.constants';
import type { ParcelEvent } from 'interfaces/ParcelEvent';
import type Shipment from 'interfaces/PmParcel';
import { getLocationConsent } from 'services/globalConfig.services';
import { getDisplayName } from 'utils/event.utils';
import { testId } from './ParcelDetails.constants';
import {
  CarrierRedirectContainer,
  City,
  CustomLocationIcon,
  CustomRecipientIcon,
  Description,
  HardCodedForMeccaContainer,
  HardCodedForMeccaLine,
  Location,
  LocationIcon,
  MainBox,
  RecipientContainer,
  RecipientIcon,
  RecipientName,
  StyledPickUpLocation,
  reactModalStyle,
} from './ParcelDetails.styles';

const { googleApiKey } = CONFIGURABLE_CONSTANTS;

// hardcoded to fix CI: https://www.pivotaltracker.com/n/projects/2485694
const HardCodedForMecca = ({
  organizationId,
  shipment,
}: {
  organizationId: number;
  shipment: Shipment;
}) => {
  const { latestMixedEvent } = shipment;
  if (
    organizationId !== 3903 ||
    !(
      latestMixedEvent.eventTypeMasterData?.key === 'E11' &&
      Date.now() - new Date(latestMixedEvent.eventTime).getTime() >= 345600000
    )
  ) {
    return null;
  }
  return (
    <HardCodedForMeccaContainer>
      <HardCodedForMeccaLine>
        Your order may take longer than usual.
      </HardCodedForMeccaLine>
      <HardCodedForMeccaLine>
        We’re experiencing delays while we implement our new warehouse
        management system. Once your order is dispatched, we will send you an
        email with your tracking information.
      </HardCodedForMeccaLine>
      <HardCodedForMeccaLine>
        Click{' '}
        <a
          href="https://www.mecca.com/en-au/customer-care/shipping-delivery/"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>{' '}
        for more information.
      </HardCodedForMeccaLine>
    </HardCodedForMeccaContainer>
  );
};

interface ParcelDetailsProps {
  shipment: Shipment;
}
export const ParcelDetails = ({ shipment }: ParcelDetailsProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { organizationId, config } = useGlobalConfigContext();
  const { carrierInfo } = useCarrierInformation();

  const [showModal, setShowModal] = useState(false);
  const getMapAllowance = () => getLocationConsent() === 'true';
  const [isMapAllowed, setIsMapAllowed] = useState(() => getMapAllowance());

  const {
    timezone,
    parcelDetailsBox: {
      shouldShowLocationPopup,
      shouldShowPickupLocation,
      locationPinIcon,
      lastEventDateFormat,
      customTextCarrierRedirect,
      shouldShowRecipientName,
      recipientIcon,
    },
    trackingDetailsBox: { shouldHideEventLocations },
  } = config;

  const { events, carrierOrg } = shipment;
  const latestEvent = events?.[0] || ({} as ParcelEvent);
  const { eventTime, parsedEventTimeTimezone } = latestEvent;

  const { convertedTime } = convertTrackingEventDateTime(
    eventTime,
    parsedEventTimeTimezone,
    timezone,
  );

  const isLocationClickable = (): boolean => {
    // in case customers update the URL param / HTML tag dynamically (without refreshing the page)
    return (
      latestEvent.long &&
      latestEvent.lat &&
      shouldShowLocationPopup &&
      isMapAllowed
    );
  };

  const onLocationClick = () => {
    if (isLocationClickable()) setShowModal(true);
  };

  const googleMapSrc = `https://www.google.com/maps/embed/v1/place?key=${googleApiKey}&q=${latestEvent.lat},${latestEvent.long}&center=${latestEvent.lat},${latestEvent.long}&zoom=10&language=${locale}`;

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (closeButtonRef.current && showModal) {
      closeButtonRef.current.focus();
    }
  }, [showModal]);

  // TODO: Use ModalV2
  return (
    <MainBox data-testid={testId}>
      <ReactModal
        onRequestClose={() => setShowModal(false)}
        style={reactModalStyle}
        isOpen={showModal}
        parentSelector={() => {
          return document.querySelector('#portalWrapper') || document.body;
        }}
        ariaHideApp={false}
        aria={{
          labelledby: 'location-modal-heading',
        }}
      >
        <HeaderModal>
          <div id="location-modal-heading">{latestEvent.location}</div>
          <CloseButton
            ref={closeButtonRef}
            onClick={() => setShowModal(false)}
            aria-label={t('ALT_TEXT_CLOSE_MODAL')}
          />
        </HeaderModal>
        <BodyModal>
          <iframe
            width="600"
            height="450"
            src={googleMapSrc}
            allowFullScreen
            title="location"
            frameBorder="0"
            style={{ border: 0 }}
          />
        </BodyModal>
      </ReactModal>

      <Description data-testid={`${testId}-time`}>
        {formatDateTime(convertedTime, lastEventDateFormat, locale)}
      </Description>
      <Typography level="displayMd" data-testid={`${testId}-displayName`}>
        {getDisplayName(latestEvent, t)}
      </Typography>

      {shouldShowRecipientName && shipment.parcel?.receivedBy && (
        <RecipientContainer data-testid={`${testId}-recipient`}>
          {recipientIcon ? (
            <CustomRecipientIcon alt="recipient icon" src={recipientIcon} />
          ) : (
            <RecipientIcon aria-hidden />
          )}
          <RecipientName>
            {t('recipient')}: <strong>{shipment.parcel.receivedBy}</strong>
          </RecipientName>
        </RecipientContainer>
      )}

      {!shouldHideEventLocations && latestEvent.location && (
        <Location
          isClickable={isLocationClickable()}
          onClick={onLocationClick}
          onMouseEnter={() => setIsMapAllowed(getMapAllowance())}
          data-testid={`${testId}-location`}
          as={isLocationClickable() ? 'button' : 'div'}
          aria-label={
            isLocationClickable()
              ? t('ALT_TEXT_LOCATION_VIEW_MORE', {
                  address: latestEvent.location,
                })
              : undefined
          }
        >
          {!locationPinIcon ? (
            <LocationIcon aria-hidden />
          ) : (
            <CustomLocationIcon alt="location icon" src={locationPinIcon} />
          )}
          <City>{latestEvent.location}</City>
        </Location>
      )}

      {shouldShowPickupLocation && <StyledPickUpLocation shipment={shipment} />}

      <HardCodedForMecca organizationId={organizationId} shipment={shipment} />

      {carrierInfo && (
        <CarrierRedirectContainer>
          <CustomText
            text={addAriaLabelToAnchors(
              customTextCarrierRedirect,
              carrierInfo?.name,
            )}
            options={{
              carrier_url: carrierInfo?.urlTracking,
            }}
          />
        </CarrierRedirectContainer>
      )}
    </MainBox>
  );
};
